/**
 * BriefingTab Component
 *
 * Calm, condensed intelligence summary. Tabular, not chippy.
 * Leads with a quiet snapshot, then grouped findings, then products.
 */

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import type { PayerContract, AuditFlag } from '../types';
import {
  calculateNextExpirationDate,
  calculateAuthBurdenScore,
  calculateCashflowRiskScore,
} from '../hooks/useContractAnalytics';
import { CitationChip } from './CitationChip';

// ─── Contract Snapshot (replaces colored chip strip) ─────────────────────────

const ContractSnapshot: React.FC<{ payer: PayerContract }> = ({ payer }) => {
  const theme = useTheme();
  const c = payer.contract;
  const exp = calculateNextExpirationDate(c.effective_date, c.initial_term_years, c.renewal_term_years, c.renewal_mechanism);
  const auth = calculateAuthBurdenScore(payer);
  const risk = calculateCashflowRiskScore(payer);
  const ratesYear = payer.reimbursement.fee_schedule_effective_date
    ? new Date(payer.reimbursement.fee_schedule_effective_date).getFullYear()
    : null;

  const renewalLabel = exp
    ? `${c.renewal_mechanism === 'auto_renew' ? 'Auto-renews' : 'Renews'} ${exp.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
    : 'Unknown';

  const items: { label: string; value: string }[] = [
    { label: 'Status', value: 'Active' },
    { label: 'Renewal', value: renewalLabel },
    { label: 'Filing Window', value: `${payer.claims_submission.timely_filing_days} days` },
    { label: 'Appeal Window', value: payer.disputes_and_appeals?.appeal_deadline_days ? `${payer.disputes_and_appeals.appeal_deadline_days} days` : '\u2014' },
    { label: 'Auth Burden', value: auth.level.charAt(0).toUpperCase() + auth.level.slice(1) },
    { label: 'Cashflow Risk', value: risk.level.charAt(0).toUpperCase() + risk.level.slice(1) },
    ...(ratesYear ? [{ label: 'Rates Since', value: String(ratesYear) }] : []),
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0,
        mb: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 1.5,
      }}
    >
      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            pr: 3,
            mr: 3,
            borderRight: idx < items.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
            py: 0.5,
            '&:last-child': { borderRight: 'none', mr: 0, pr: 0 },
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.8, lineHeight: 1.2 }}>
            {item.label}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, mt: 0.25 }}>
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ─── Findings Section (replaces alerts feed + unusual provisions) ────────────

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, info: 3 };
const SEVERITY_LABEL: Record<string, string> = { critical: 'Critical', high: 'High', medium: 'Medium', info: 'Info' };

/**
 * Groups flags by severity and renders each group as a collapsible section.
 * Critical is expanded by default; others are collapsed.
 */
const FindingsSection: React.FC<{ payer: PayerContract }> = ({ payer }) => {
  const theme = useTheme();
  const allFlags = [...(payer.audit_flags || [])].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );

  // Also merge unusual provisions as "info" severity findings
  const unusualAsFlags: AuditFlag[] = (payer.unusual_provisions || []).map(p => ({
    severity: 'info' as const,
    category: 'unusual_provision',
    description: `${p.provision} \u2014 ${p.why_unusual}`,
    citation: p.citation,
  }));

  const flags = [...allFlags, ...unusualAsFlags];
  if (flags.length === 0) return null;

  // Group by severity
  const groups = new Map<string, AuditFlag[]>();
  flags.forEach(f => {
    const existing = groups.get(f.severity) || [];
    existing.push(f);
    groups.set(f.severity, existing);
  });

  const severityKeys = ['critical', 'high', 'medium', 'info'].filter(s => groups.has(s));

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 1, display: 'block' }}>
        Contract Findings
      </Typography>
      {severityKeys.map(severity => (
        <FindingsGroup
          key={severity}
          severity={severity}
          flags={groups.get(severity) || []}
          payer={payer}
          defaultExpanded={severity === 'critical'}
        />
      ))}
    </Box>
  );
};

const FindingsGroup: React.FC<{
  severity: string;
  flags: AuditFlag[];
  payer: PayerContract;
  defaultExpanded: boolean;
}> = ({ severity, flags, payer, defaultExpanded }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Subtle dot color only -- no loud icons
  const dotColor: Record<string, string> = {
    critical: theme.palette.error.main,
    high: theme.palette.warning.main,
    medium: theme.palette.info.main,
    info: theme.palette.grey[400],
  };

  return (
    <Box sx={{ mb: 0.5 }}>
      {/* Group header -- clickable, quiet */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          px: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: 1,
          '&:hover': { bgcolor: theme.palette.action.hover },
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor[severity], flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          {SEVERITY_LABEL[severity]} ({flags.length})
        </Typography>
        {expanded ? <KeyboardArrowUp sx={{ fontSize: 18, color: 'text.secondary' }} /> : <KeyboardArrowDown sx={{ fontSize: 18, color: 'text.secondary' }} />}
      </Box>

      {/* Collapsed table of findings */}
      <Collapse in={expanded}>
        <Table size="small" sx={{ ml: 2.5 }}>
          <TableBody>
            {flags.map((flag, idx) => (
              <TableRow key={idx} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: theme.palette.action.hover } }}>
                <TableCell sx={{ py: 1, pl: 0, border: 'none', borderBottom: `1px solid ${theme.palette.divider}`, verticalAlign: 'top' }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.5, color: 'text.primary' }}>
                    {flag.description}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1, pr: 0, border: 'none', borderBottom: `1px solid ${theme.palette.divider}`, width: 180, verticalAlign: 'top' }}>
                  {flag.citation && (
                    <CitationChip
                      citation={Array.isArray(flag.citation) ? flag.citation[0] : flag.citation}
                      documentRegistry={payer.document_registry}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
};

// ─── Products Summary ────────────────────────────────────────────────────────

const ProductsSummary: React.FC<{ payer: PayerContract }> = ({ payer }) => {
  const theme = useTheme();
  if (!payer.products || payer.products.length === 0) return null;

  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5, mb: 1, display: 'block' }}>
        Covered Products
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {payer.products.map(p => p.product_name).join(' \u00b7 ')}
      </Typography>
    </Box>
  );
};

// ─── Main Export ─────────────────────────────────────────────────────────────

interface BriefingTabProps {
  payer: PayerContract;
}

export const BriefingTab: React.FC<BriefingTabProps> = ({ payer }) => {
  return (
    <Box>
      <ContractSnapshot payer={payer} />
      <FindingsSection payer={payer} />
      <ProductsSummary payer={payer} />
    </Box>
  );
};
